/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authService, AuthError } from "@/services/authService";
import { LoginFormValues } from "@/lib/validations/auth";
import { StatusResponse } from "@/types/status";
import { getStatusMessage } from "@/utils/messageHelper";

export interface UserData {
    _id: string;
    username: string;
    name: string;
    email?: string;
    role?: any;
    permission?: any;
}

interface AuthState {
    // State
    user: UserData | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    // Actions
    login: (data: LoginFormValues) => Promise<boolean>;
    logout: () => Promise<void>;
    refreshAccessToken: () => Promise<boolean>;
    checkTokenValidity: () => Promise<boolean>;
    clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            // Initial state
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            // Actions
            login: async (data: LoginFormValues) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await authService.login(data);
                    console.log("response: ", response);
                    if (response?.accessToken) {
                        // Lưu token
                        set({
                            accessToken: response.accessToken,
                            refreshToken: response.refreshToken || null,
                            isAuthenticated: true,
                            isLoading: false
                        });

                        // Lưu thông tin người dùng từ response
                        if (response.userData) {
                            set({ user: response.userData });
                        }
                        // Lưu permissions vào localStorage nếu có
                        if (response.permission) {
                            localStorage.setItem('permission', JSON.stringify(response.permission));
                        }

                        return true;
                    } else {
                        // Nếu không thành công, throw AuthError để xử lý đúng cách
                        const status = response?.status || StatusResponse.FAIL;
                        throw new AuthError(
                            status,
                            getStatusMessage(status, response?.message),
                            response?.message
                        );
                    }
                } catch (error) {
                    console.log("error; ", error)
                    if (error instanceof AuthError) {
                        // Sử dụng message đã được dịch từ AuthError
                        set({ error: error.message, isLoading: false });
                    } else {
                        // Lỗi không mong đợi
                        set({ error: "Có lỗi xảy ra khi đăng nhập", isLoading: false });
                    }
                    throw error;
                }
            },

            logout: async () => {
                const { accessToken } = get();
                set({ isLoading: true });

                try {
                    if (accessToken) {
                        await authService.logout(accessToken);
                    }
                } catch (error) {
                    console.error("Logout error:", error);
                } finally {
                    set({
                        user: null,
                        accessToken: null,
                        refreshToken: null,
                        isAuthenticated: false,
                        isLoading: false
                    });

                    // Clear permissions from localStorage
                    localStorage.removeItem('permission');
                }
            },

            refreshAccessToken: async () => {
                const { refreshToken } = get();
                if (!refreshToken) return false;

                try {
                    const response = await authService.refreshToken(refreshToken);

                    if (response.accessToken) {
                        set({
                            accessToken: response.accessToken,
                            isAuthenticated: true
                        });
                        return true;
                    }
                    return false;
                } catch (error) {
                    console.error("Token refresh error:", error);
                    // Nếu refresh token thất bại, logout user
                    set({
                        user: null,
                        accessToken: null,
                        refreshToken: null,
                        isAuthenticated: false,
                        isLoading: false
                    });
                    return false;
                }
            },

            // Kiểm tra tính hợp lệ của token bằng cách gọi API /me
            checkTokenValidity: async () => {
                const { accessToken } = get();
                if (!accessToken) {
                    console.log("❌ No access token in store");
                    return false;
                }

                try {
                    console.log("🔍 Calling getUserInfo with token...");
                    const userInfo = await authService.getUserInfo(accessToken);
                    console.log("📄 getUserInfo response:", userInfo);

                    if (userInfo) {
                        console.log("✅ User info received, updating store");
                        set({ user: userInfo });
                        return true;
                    }
                    console.log("❌ No user info received");
                    return false;
                } catch (error) {
                    console.error("❌ Token validity check failed:", error);
                    // Token không hợp lệ, thử refresh
                    console.log("🔄 Attempting token refresh...");
                    const refreshSuccess = await get().refreshAccessToken();
                    console.log("🔄 Token refresh result:", refreshSuccess);
                    return refreshSuccess;
                }
            },

            clearError: () => set({ error: null })
        }),
        {
            name: "auth-storage", // tên của item trong localStorage
            partialize: (state) => ({
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                user: state.user,
                isAuthenticated: state.isAuthenticated
            })
        }
    )
);
